import {
  OktonCollapseCard as CollapseCard,
  OktonInput,
  OktonSelect,
  OktonTextarea,
} from '@okton/platform-ui';
import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { ArrowLeft, Copy, ShieldCheck } from 'lucide-react';
import { useHistory } from 'react-router-dom';

import Page from '../../layout/IntegraAppPage';
import { useAuth } from '../../hooks/auth';
import { useTokensIntegra } from '../../services/hooks/useTokensIntegra';

const DOCUMENTACAO_PERMISSOES: Record<
  string,
  {
    descricao: string;
    endpoints: string[];
  }
> = {
  'integra:produtos:read': {
    descricao: 'Permite consultar os dados de um produto pelo ID.',
    endpoints: ['GET /api/integra/v1/produtos/{id}'],
  },
  'integra:produtos:create': {
    descricao: 'Permite criar produtos individualmente ou em lote.',
    endpoints: [
      'POST /api/integra/v1/produtos',
      'POST /api/integra/v1/produtos/lote',
    ],
  },
  'integra:produtos:update': {
    descricao: 'Permite atualizar os dados de um produto existente.',
    endpoints: ['PATCH /api/integra/v1/produtos/{id}'],
  },
  'integra:produtos:delete': {
    descricao: 'Permite excluir produtos.',
    endpoints: ['DELETE /api/integra/v1/produtos/{id}'],
  },
  'integra:pedidos:read': {
    descricao: 'Permite listar pedidos e consultar os detalhes de um pedido.',
    endpoints: [
      'GET /api/integra/v1/pedidos',
      'GET /api/integra/v1/pedidos/{id}',
    ],
  },
};

const ENDPOINTS_API_INTEGRA = [
  {
    metodo: 'GET',
    endpoint: '/api/integra/v1/pedidos',
    permissao: 'integra:pedidos:read',
    colorScheme: 'blue',
  },
  {
    metodo: 'GET',
    endpoint: '/api/integra/v1/pedidos/{id}',
    permissao: 'integra:pedidos:read',
    colorScheme: 'blue',
  },
  {
    metodo: 'GET',
    endpoint: '/api/integra/v1/produtos/{id}',
    permissao: 'integra:produtos:read',
    colorScheme: 'blue',
  },
  {
    metodo: 'POST',
    endpoint: '/api/integra/v1/produtos',
    permissao: 'integra:produtos:create',
    colorScheme: 'green',
  },
  {
    metodo: 'POST',
    endpoint: '/api/integra/v1/produtos/lote',
    permissao: 'integra:produtos:create',
    colorScheme: 'green',
  },
  {
    metodo: 'PATCH',
    endpoint: '/api/integra/v1/produtos/{id}',
    permissao: 'integra:produtos:update',
    colorScheme: 'orange',
  },
  {
    metodo: 'DELETE',
    endpoint: '/api/integra/v1/produtos/{id}',
    permissao: 'integra:produtos:delete',
    colorScheme: 'red',
  },
];

const GerarTokenIntegra: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const toast = useToast();

  const pageBg = useColorModeValue('#F4F8FB', 'gray.900');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const permissionBg = useColorModeValue('white', 'gray.800');
  const permissionHoverBg = useColorModeValue('gray.50', 'gray.700');
  const permissionBorder = useColorModeValue('gray.200', 'gray.600');
  const permissionTitle = useColorModeValue('gray.800', 'gray.100');
  const abilityColor = useColorModeValue('blue.600', 'blue.300');

  const successBg = useColorModeValue('green.50', 'rgba(16, 185, 129, 0.14)');
  const successColor = useColorModeValue('green.800', 'green.100');
  const successBorder = useColorModeValue('green.200', 'green.700');

  const [nomeToken, setNomeToken] = useState('');
  const [duracaoToken, setDuracaoToken] = useState('1');
  const [abilitiesSelecionadas, setAbilitiesSelecionadas] = useState<string[]>(
    [],
  );
  const [tokenGerado, setTokenGerado] = useState('');

  const { opcoesPermissoes, carregandoPermissoes, gerandoToken, gerarToken } =
    useTokensIntegra({
      empresaId: user?.empresa_id,
      filialId: user?.filial_id,
    });

  const todasSelecionadas = useMemo(
    () =>
      opcoesPermissoes.length > 0 &&
      abilitiesSelecionadas.length === opcoesPermissoes.length,
    [abilitiesSelecionadas, opcoesPermissoes],
  );

  const calcularExpiracaoNovoToken = () => {
    const data = new Date();
    data.setMonth(data.getMonth() + Number(duracaoToken));

    return data.toLocaleDateString('pt-BR');
  };

  const alternarTodasPermissoes = () => {
    setAbilitiesSelecionadas(
      todasSelecionadas
        ? []
        : opcoesPermissoes.map(permissao => permissao.chave),
    );
  };

  const alternarPermissao = (chave: string) => {
    setAbilitiesSelecionadas(anteriores =>
      anteriores.includes(chave)
        ? anteriores.filter(item => item !== chave)
        : [...anteriores, chave],
    );
  };

  const aplicarPreset = (tipo: 'leitura' | 'escrita' | 'completo') => {
    if (tipo === 'completo') {
      setAbilitiesSelecionadas(opcoesPermissoes.map(item => item.chave));
      return;
    }

    if (tipo === 'leitura') {
      setAbilitiesSelecionadas(
        opcoesPermissoes
          .filter(item => item.chave.includes(':read'))
          .map(item => item.chave),
      );
      return;
    }

    setAbilitiesSelecionadas(
      opcoesPermissoes
        .filter(
          item =>
            item.chave.includes(':create') ||
            item.chave.includes(':update') ||
            item.chave.includes(':delete'),
        )
        .map(item => item.chave),
    );
  };

  const handleGerarToken = async () => {
    if (!user?.empresa_id || !user?.filial_id) {
      toast({
        title: 'Empresa ou filial não identificada.',
        description: 'Faça login novamente antes de gerar o token.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
      return;
    }

    if (!nomeToken.trim()) {
      toast({
        title: 'Informe o nome do token.',
        status: 'warning',
        position: 'bottom-right',
        isClosable: true,
      });
      return;
    }

    if (abilitiesSelecionadas.length === 0) {
      toast({
        title: 'Selecione ao menos uma permissão.',
        status: 'warning',
        position: 'bottom-right',
        isClosable: true,
      });
      return;
    }

    try {
      setTokenGerado('');

      const response = await gerarToken({
        nome: nomeToken.trim(),
        duracao_meses: Number(duracaoToken),
        abilities: abilitiesSelecionadas,
      });

      setTokenGerado(response.access_token);
      setNomeToken('');
      setAbilitiesSelecionadas([]);

      toast({
        title: 'Token gerado com sucesso.',
        status: 'success',
        position: 'bottom-right',
        isClosable: true,
      });
    } catch {
      toast({
        title: 'Não foi possível gerar o token.',
        description: 'Revise as permissões selecionadas e tente novamente.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    }
  };

  const copiarToken = async () => {
    if (!tokenGerado) return;

    try {
      await navigator.clipboard.writeText(tokenGerado);

      toast({
        title: 'Token copiado.',
        status: 'success',
        position: 'bottom-right',
        isClosable: true,
      });
    } catch {
      toast({
        title: 'Não foi possível copiar o token.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    }
  };

  return (
    <Page>
      <Box bg={pageBg} minH="100vh" p={{ base: 4, md: 6 }} color={textColor}>
        <HStack spacing={3} mb={6} align="flex-start">
          <Button
            leftIcon={<ArrowLeft size={16} />}
            variant="ghost"
            size="sm"
            onClick={() => history.push('/tokens/integra')}
          >
            Voltar
          </Button>

          <Box>
            <Heading size="md">Gerar token</Heading>
            <Text fontSize="sm" color={mutedColor}>
              Configure as permissões e consulte as instruções da API.
            </Text>
          </Box>
        </HStack>

        <Stack spacing={5}>
          <CollapseCard
            title="Contexto do token"
            subtitle="Empresa, filial e validade da nova credencial."
            defaultOpen
          >
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box>
                <Text fontSize="xs" color={mutedColor}>
                  Empresa
                </Text>
                <Text mt={1} fontWeight="semibold">
                  #{user?.empresa_id || '-'}
                </Text>
              </Box>

              <Box>
                <Text fontSize="xs" color={mutedColor}>
                  Filial
                </Text>
                <Text mt={1} fontWeight="semibold">
                  #{user?.filial_id || '-'}
                </Text>
              </Box>

              <HStack align="flex-start" justify="space-between">
                <Box>
                  <Text fontSize="xs" color={mutedColor}>
                    Expiração
                  </Text>
                  <Text mt={1} fontWeight="semibold">
                    {calcularExpiracaoNovoToken()}
                  </Text>
                </Box>

                <Icon as={ShieldCheck} color="blue.400" boxSize={5} />
              </HStack>
            </SimpleGrid>
          </CollapseCard>

          <CollapseCard
            title="Dados do token"
            subtitle="Identifique a integração e defina o prazo de validade."
            defaultOpen
          >
            <Stack spacing={5}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                <OktonInput
                  name="nomeToken"
                  label="Nome do token"
                  required
                  value={nomeToken}
                  onChange={event => setNomeToken(event.target.value)}
                  placeholder="Ex: Integração ERP - leitura"
                />

                <OktonSelect
                  name="duracaoToken"
                  label="Duração"
                  required
                  value={duracaoToken}
                  onChange={event => setDuracaoToken(event.target.value)}
                  options={[
                    { value: '1', label: '1 mês' },
                    { value: '3', label: '3 meses' },
                    { value: '6', label: '6 meses' },
                  ]}
                />
              </SimpleGrid>

              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontWeight="semibold">Exibição única</Text>
                  <Text fontSize="sm">
                    O token completo será exibido apenas uma vez após a geração.
                  </Text>
                </Box>
              </Alert>
            </Stack>
          </CollapseCard>

          <CollapseCard
            title="Permissões"
            subtitle="Defina quais recursos da API poderão ser acessados."
            defaultOpen
          >
            <Stack spacing={4}>
              <Flex
                justify="space-between"
                align={{ base: 'stretch', md: 'center' }}
                direction={{ base: 'column', md: 'row' }}
                gap={3}
              >
                <HStack spacing={2} flexWrap="wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => aplicarPreset('leitura')}
                    isDisabled={carregandoPermissoes}
                  >
                    Somente leitura
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => aplicarPreset('escrita')}
                    isDisabled={carregandoPermissoes}
                  >
                    Escrita
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => aplicarPreset('completo')}
                    isDisabled={carregandoPermissoes}
                  >
                    Completo
                  </Button>
                </HStack>

                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="blue"
                  onClick={alternarTodasPermissoes}
                  isDisabled={
                    carregandoPermissoes || opcoesPermissoes.length === 0
                  }
                >
                  {todasSelecionadas ? 'Limpar todas' : 'Selecionar todas'}
                </Button>
              </Flex>

              <Divider />

              {carregandoPermissoes ? (
                <Stack spacing={3}>
                  <Skeleton h="60px" borderRadius="md" />
                  <Skeleton h="60px" borderRadius="md" />
                  <Skeleton h="60px" borderRadius="md" />
                </Stack>
              ) : opcoesPermissoes.length === 0 ? (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  Nenhuma permissão disponível para criação de tokens.
                </Alert>
              ) : (
                <Accordion allowMultiple>
                  {opcoesPermissoes.map(permissao => {
                    const selecionada = abilitiesSelecionadas.includes(
                      permissao.chave,
                    );

                    const documentacao =
                      DOCUMENTACAO_PERMISSOES[permissao.chave];

                    return (
                      <AccordionItem
                        key={permissao.chave}
                        borderWidth="1px"
                        borderColor={permissionBorder}
                        borderRadius="md"
                        mb={3}
                        overflow="hidden"
                        bg={permissionBg}
                      >
                        <Flex align="center">
                          <AccordionButton
                            flex="1"
                            px={4}
                            py={3}
                            _hover={{ bg: permissionHoverBg }}
                            _expanded={{ bg: permissionHoverBg }}
                          >
                            <Box flex="1" textAlign="left">
                              <Text
                                fontWeight="semibold"
                                color={permissionTitle}
                              >
                                {permissao.nome}
                              </Text>

                              <Text fontSize="xs" color={abilityColor}>
                                {permissao.chave}
                              </Text>
                            </Box>

                            <AccordionIcon color={mutedColor} />
                          </AccordionButton>

                          <Checkbox
                            mr={4}
                            colorScheme="blue"
                            isChecked={selecionada}
                            onChange={() => alternarPermissao(permissao.chave)}
                            aria-label={`Selecionar ${permissao.nome}`}
                          />
                        </Flex>

                        <AccordionPanel px={4} pb={4}>
                          <Stack spacing={3}>
                            <Text fontSize="sm" color={mutedColor}>
                              {documentacao?.descricao || permissao.descricao}
                            </Text>

                            <HStack spacing={2} flexWrap="wrap">
                              {(documentacao?.endpoints || []).map(endpoint => (
                                <Badge
                                  key={endpoint}
                                  colorScheme={permissao.colorScheme}
                                  textTransform="none"
                                >
                                  {endpoint}
                                </Badge>
                              ))}
                            </HStack>
                          </Stack>
                        </AccordionPanel>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}

              <Flex justify="flex-end">
                <Button
                  colorScheme="blue"
                  onClick={handleGerarToken}
                  isLoading={gerandoToken}
                  isDisabled={
                    carregandoPermissoes || opcoesPermissoes.length === 0
                  }
                >
                  Gerar token
                </Button>
              </Flex>
            </Stack>
          </CollapseCard>

          <CollapseCard
            title="Como usar o token"
            subtitle="Envie o token no header Authorization em todas as chamadas da API."
            defaultOpen={false}
          >
            <Stack spacing={5}>
              <Box>
                <Heading size="xs" mb={3}>
                  Headers obrigatórios
                </Heading>

                <Stack spacing={2}>
                  <Box
                    bg={subtleBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    p={3}
                  >
                    <Text fontFamily="mono" fontSize="sm">
                      Authorization: Bearer SEU_TOKEN
                    </Text>
                  </Box>

                  <Box
                    bg={subtleBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    p={3}
                  >
                    <Text fontFamily="mono" fontSize="sm">
                      Accept: application/json
                    </Text>
                  </Box>

                  <Box
                    bg={subtleBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    p={3}
                  >
                    <Text fontFamily="mono" fontSize="sm">
                      Content-Type: application/json
                    </Text>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Heading size="xs" mb={3}>
                  Endpoints disponíveis
                </Heading>

                <Stack spacing={3}>
                  {ENDPOINTS_API_INTEGRA.map(item => (
                    <Flex
                      key={`${item.metodo}-${item.endpoint}`}
                      align={{ base: 'flex-start', md: 'center' }}
                      justify="space-between"
                      direction={{ base: 'column', md: 'row' }}
                      gap={3}
                      bg={subtleBg}
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="md"
                      p={3}
                    >
                      <HStack spacing={3}>
                        <Badge colorScheme={item.colorScheme}>
                          {item.metodo}
                        </Badge>

                        <Text fontFamily="mono" fontSize="sm">
                          {item.endpoint}
                        </Text>
                      </HStack>

                      <Badge colorScheme="purple" textTransform="none">
                        {item.permissao}
                      </Badge>
                    </Flex>
                  ))}
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Heading size="xs" mb={3}>
                  Respostas de erro
                </Heading>

                <Stack spacing={3}>
                  <Box
                    bg={subtleBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    p={3}
                  >
                    <Badge colorScheme="red" mb={2}>
                      401
                    </Badge>

                    <Text fontSize="sm" color={mutedColor}>
                      Token inválido, expirado ou revogado.
                    </Text>
                  </Box>

                  <Box
                    bg={subtleBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    p={3}
                  >
                    <Badge colorScheme="orange" mb={2}>
                      403
                    </Badge>

                    <Text fontSize="sm" color={mutedColor}>
                      Token válido, mas sem permissão para acessar o recurso.
                    </Text>
                  </Box>

                  <Box
                    bg={subtleBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    p={3}
                  >
                    <Badge colorScheme="gray" mb={2}>
                      404
                    </Badge>

                    <Text fontSize="sm" color={mutedColor}>
                      Recurso ou rota não encontrada.
                    </Text>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </CollapseCard>

          {tokenGerado && (
            <CollapseCard
              title="Token gerado"
              subtitle="Copie a credencial agora. Ela não será exibida novamente."
              collapsible={false}
            >
              <Stack spacing={4}>
                <Alert
                  status="success"
                  borderRadius="md"
                  bg={successBg}
                  color={successColor}
                  borderWidth="1px"
                  borderColor={successBorder}
                >
                  <AlertIcon />
                  Token criado com sucesso.
                </Alert>

                <OktonTextarea
                  name="tokenGerado"
                  value={tokenGerado}
                  isReadOnly
                  minH="110px"
                  fontFamily="mono"
                  fontSize="sm"
                  resize="none"
                />

                <Flex justify="flex-end">
                  <Button leftIcon={<Copy size={16} />} onClick={copiarToken}>
                    Copiar token
                  </Button>
                </Flex>
              </Stack>
            </CollapseCard>
          )}
        </Stack>
      </Box>
    </Page>
  );
};

export default GerarTokenIntegra;
